import React, { useEffect, useRef, useState } from "react";
import AlertModal, { type AlertModalType } from "./AlertModal";
import SubscriptionSuccessModal from "./SubscriptionSuccessModal";
import { auth } from "../lib/auth";
import { trackSubscriptionSuccess } from "../lib/analytics";

interface WebSocketNotificationProps {
  msisdn?: string | null;
  username?: string | null;
}

const WEBSOCKET_BASE_URL = "wss://ws.yours4u.com/ws/notifications/";

export const WebSocketNotification: React.FC<WebSocketNotificationProps> = ({ msisdn, username }) => {
  const [visible, setVisible] = useState(false);
  const [message, setMessage] = useState("");
  const [title, setTitle] = useState<string>("Notification");
  const [alertType, setAlertType] = useState<AlertModalType>("info");

  const [successVisible, setSuccessVisible] = useState(false);
  const [successTitle, setSuccessTitle] = useState<string | undefined>(undefined);
  const [successMessage, setSuccessMessage] = useState<string | undefined>(undefined);

  const wsRef = useRef<WebSocket | null>(null);

  useEffect(() => {
    if (!import.meta.env.DEV) return;

    const handleTestNotification = (event: CustomEvent<any>) => {
      const data = event.detail || {};
      if (data.type === "success") {
        setSuccessTitle(data.title || "Subscription Successful");
        setSuccessMessage(data.message || "Your unlimited music subscription has been activated successfully.");
        setSuccessVisible(true);
      } else {
        setTitle(data.title || "Notification");
        setMessage(data.message || "This is a test notification alert from the server.");
        setAlertType(data.type || "info");
        setVisible(true);
      }
    };

    window.addEventListener("ym:test-notification" as any, handleTestNotification);
    return () => {
      window.removeEventListener("ym:test-notification" as any, handleTestNotification);
    };
  }, []);

  useEffect(() => {
    let identifier: string | null = null;
    let wsUrl: string | null = null;

    if (msisdn) {
      identifier = msisdn;
      wsUrl = `${WEBSOCKET_BASE_URL}${msisdn}/`;
    } else if (username) {
      identifier = username;
      wsUrl = `${WEBSOCKET_BASE_URL}username/${username}/`;
    }

    if (!identifier || !wsUrl) {
      return;
    }

    console.log("Connecting to WebSocket with identifier:", identifier);
    try {
      const socket = new WebSocket(wsUrl);
      wsRef.current = socket;

      socket.onopen = () => {
        console.log("WebSocket connected", wsUrl);
      };

      socket.onmessage = (event) => {
        try {
          let data = JSON.parse(event.data);
          if (typeof data.message === "string") {
            try {
              data = JSON.parse(data.message);
            } catch {
              // message is plain string
            }
          }

          console.log("WebSocket message data:", data);

          if (data.type === "success") {
            const currentUser = auth.user();
            if (currentUser) {
              auth.saveUser({ ...currentUser, is_premium: true });
            } else {
              try {
                localStorage.setItem("user_status", JSON.stringify({ is_premium: true }));
              } catch {}
            }
            if (typeof window !== "undefined") {
              window.dispatchEvent(new CustomEvent("ym_quota_updated"));
            }
            trackSubscriptionSuccess({
              transactionId: data.transaction_id || data.clientTransactionId,
              value: data.amount || 0,
              itemName: data.title || "Yours Music Premium",
              paymentMethod: data.payment_method || "WebSocket Notification",
              productCode: data.product_code || data.productCode,
            });
            setTitle(data.title || "");
            setMessage(data.message || "");
            setVisible(false);
            setAlertType("success");
            setSuccessTitle(data.title || "Subscription Successful");
            setSuccessMessage(data.message || "");
            setSuccessVisible(true);
          } else {
            setTitle(data.title || "Notification");
            setMessage(data.message || event.data);
            setAlertType("info");
            setVisible(true);
          }
        } catch {
          setTitle("Notification");
          setMessage(event.data);
          setAlertType("info");
          setVisible(true);
        }
      };

      socket.onerror = (err) => {
        console.warn("WebSocket error:", err);
      };

      socket.onclose = () => {
        console.log("WebSocket closed");
      };
    } catch (e) {
      console.warn("Failed to initialize WebSocket:", e);
    }

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [msisdn, username]);

  return (
    <>
      <AlertModal
        visible={visible}
        onClose={() => setVisible(false)}
        title={title}
        message={message}
        type={alertType}
      />

      <SubscriptionSuccessModal
        visible={successVisible}
        title={successTitle}
        message={successMessage}
        onClose={() => setSuccessVisible(false)}
      />
    </>
  );
};

export default WebSocketNotification;
